import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Download, X, Smartphone } from 'lucide-react';
import { cn } from '../lib/utils';

const PWAInstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);

  useEffect(() => {
    // Check if running on iOS
    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(isIOSDevice);

    // Check if already installed
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || 
                         window.navigator.standalone === true;
    
    // Check if user dismissed the prompt before
    const dismissed = localStorage.getItem('trebol_pwa_dismissed');
    const dismissedDate = dismissed ? new Date(parseInt(dismissed)) : null;
    const daysSinceDismissed = dismissedDate ? 
      (Date.now() - dismissedDate.getTime()) / (1000 * 60 * 60 * 24) : null;
    
    // Show again after 7 days
    const shouldShow = !dismissed || (daysSinceDismissed && daysSinceDismissed > 7);

    if (isStandalone) {
      return; // Already installed, don't show
    }

    // For iOS, show custom instructions
    if (isIOSDevice && shouldShow) {
      // Delay showing the prompt
      const timer = setTimeout(() => {
        setShowPrompt(true);
      }, 3000);
      return () => clearTimeout(timer);
    }

    // For other browsers, listen for the beforeinstallprompt event
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      if (shouldShow) {
        setTimeout(() => setShowPrompt(true), 3000);
      }
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, []);

  const handleInstall = async () => {
    if (isIOS) {
      setShowIOSInstructions(true);
      return;
    }

    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      console.log('[TREBOL] PWA installed');
    }
    
    setDeferredPrompt(null);
    setShowPrompt(false);
  };

  const handleDismiss = () => {
    localStorage.setItem('trebol_pwa_dismissed', Date.now().toString());
    setShowPrompt(false);
    setShowIOSInstructions(false);
  };

  if (!showPrompt) return null;

  return (
    <>
      {/* Main Install Banner */}
      <div 
        className={cn(
          "fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 z-50",
          "bg-white border border-slate-200 rounded-sm shadow-lg p-4",
          "animate-in slide-in-from-bottom-4 duration-300"
        )}
        data-testid="pwa-install-prompt"
      >
        <button 
          onClick={handleDismiss}
          className="absolute top-2 right-2 p-1 text-slate-400 hover:text-slate-600"
        >
          <X size={16} />
        </button>
        
        <div className="flex items-start gap-3">
          <div className="w-12 h-12 bg-emerald-50 rounded-sm flex items-center justify-center flex-shrink-0">
            <Smartphone className="w-6 h-6 text-emerald-900" strokeWidth={1.5} />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-slate-900 text-sm">
              Instalar TREBOL FINANCE
            </h3>
            <p className="text-xs text-slate-500 mt-1">
              Accede más rápido desde tu pantalla de inicio. Funciona sin conexión.
            </p>
            <div className="flex gap-2 mt-3">
              <Button
                onClick={handleInstall}
                size="sm"
                className="bg-emerald-900 hover:bg-emerald-800 rounded-sm text-xs"
              >
                <Download size={14} className="mr-1.5" />
                Instalar App
              </Button>
              <Button
                onClick={handleDismiss}
                variant="ghost"
                size="sm"
                className="text-xs text-slate-500"
              >
                Ahora no
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* iOS Instructions Modal */}
      {showIOSInstructions && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div 
            className="bg-white rounded-sm shadow-xl mx-4 p-6 max-w-sm w-full animate-in zoom-in-95 duration-200"
            data-testid="ios-install-instructions"
          >
            <div className="flex justify-between items-start mb-4">
              <h3 className="font-semibold text-slate-900">
                Instalar en iOS
              </h3>
              <button onClick={handleDismiss} className="text-slate-400">
                <X size={20} />
              </button>
            </div>
            
            <div className="space-y-4 text-sm text-slate-600">
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-xs font-bold text-emerald-900">1</span>
                </div>
                <p>
                  Toca el botón <strong>Compartir</strong> 
                  <span className="inline-block mx-1 px-1.5 py-0.5 bg-slate-100 rounded text-xs">⬆</span>
                  en la barra de Safari
                </p>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-xs font-bold text-emerald-900">2</span>
                </div>
                <p>
                  Desplázate hacia abajo y selecciona <strong>"Añadir a pantalla de inicio"</strong>
                </p>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-xs font-bold text-emerald-900">3</span>
                </div>
                <p>
                  Toca <strong>"Añadir"</strong> en la esquina superior derecha
                </p>
              </div>
            </div>
            
            <Button
              onClick={handleDismiss}
              className="w-full mt-6 bg-emerald-900 hover:bg-emerald-800 rounded-sm"
            >
              Entendido
            </Button>
          </div>
        </div>
      )}
    </>
  );
};

export default PWAInstallPrompt;
